import React from 'react';
import { Button, Card } from 'react-bootstrap';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

const styles = `
@keyframes relcoreErrorFloat {
  0% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0); }
}

@keyframes relcoreErrorRingSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.relcore-route-error {
  min-height: calc(100vh - 140px);
}

.relcore-route-error-card {
  border: 0;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.relcore-route-error-icon {
  width: 84px;
  height: 84px;
  position: relative;
  animation: relcoreErrorFloat 2.8s ease-in-out infinite;
}

.relcore-route-error-icon-ring {
  position: absolute;
  inset: -10px;
  border-radius: 999px;
  border: 2px dashed currentColor;
  opacity: 0.35;
  animation: relcoreErrorRingSpin 12s linear infinite;
}

.relcore-route-error-icon i {
  font-size: 2.1rem;
}
`;

type RouteErrorCta = {
  label: string;
  to: string;
  variant?: 'primary' | 'secondary' | 'outline-secondary' | 'outline-primary';
};

type RouteErrorCopy = {
  title: string;
  message: string;
  iconClassName?: string;
  tone?: 'warning' | 'danger' | 'info';
};

export type RouteErrorPageProps = {
  notFound?: RouteErrorCopy;
  badRequest?: RouteErrorCopy;
  fallback?: RouteErrorCopy;
  primaryCta?: RouteErrorCta;
  secondaryCta?: RouteErrorCta;
};

export default function RouteErrorPage(props: RouteErrorPageProps): React.ReactElement {
  const error = useRouteError();
  const user = useUser();

  const fallback: RouteErrorCopy = props.fallback ?? {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Please try again.',
    iconClassName: 'fas fa-exclamation-triangle',
    tone: 'danger',
  };

  const notFound: RouteErrorCopy = props.notFound ?? {
    title: 'Not found',
    message: 'The requested page could not be found.',
    iconClassName: 'fas fa-search',
    tone: 'warning',
  };

  const badRequest: RouteErrorCopy = props.badRequest ?? {
    title: 'Invalid request',
    message: 'The URL is missing required information, or it is invalid.',
    iconClassName: 'fas fa-link',
    tone: 'warning',
  };

  let copy: RouteErrorCopy = fallback;
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      copy = notFound;
      copy.message = error.data as string ?? copy.message;
    } else if (error.status === 400) {
      copy = badRequest;
    }
  }

  const tone = copy.tone ?? 'warning';
  const iconClassName = copy.iconClassName ?? 'fas fa-exclamation-triangle';

  const primaryCta = props.primaryCta;
  const secondaryCta = props.secondaryCta;

  const shouldShowCta = (cta: RouteErrorCta | undefined): cta is RouteErrorCta => {
    if (!cta) return false;
    if (user && cta.to.startsWith('/login')) return false;
    return true;
  };

  const ctas = [primaryCta, secondaryCta].filter(shouldShowCta);
  const hasCtas = ctas.length > 0;

  const toneBgClass =
    tone === 'danger' ? 'bg-danger' : tone === 'info' ? 'bg-info' : 'bg-warning';
  const toneTextClass =
    tone === 'danger' ? 'text-danger' : tone === 'info' ? 'text-info' : 'text-warning';

  return (
    <div className="relcore-route-error d-flex align-items-center justify-content-center py-5">
      <style>{styles}</style>
      <Card style={{ maxWidth: 760 }} className="relcore-route-error-card w-100 shadow-lg">
        <Card.Body className="p-4 p-md-5">
          <div className={`d-flex ${hasCtas ? 'align-items-start' : 'align-items-center'} gap-4 gap-md-5`}>
            <div
              className={`relcore-route-error-icon rounded-circle ${toneBgClass} bg-opacity-25 d-flex align-items-center justify-content-center flex-shrink-0 ${toneTextClass}`}>
              <div className="relcore-route-error-icon-ring" aria-hidden />
              <i className={`fas fa-fw ${iconClassName}`} aria-hidden />
            </div>
            <div
              className={`flex-grow-1 ${hasCtas ? '' : 'd-flex flex-column justify-content-center pl-2'}`}
              style={!hasCtas ? { minHeight: 84 } : undefined}>
              <h1 className="h4 mb-2">{copy.title}</h1>
              <p className={`text-muted ${hasCtas ? 'mb-4' : 'mb-0'}`}>{copy.message}</p>
              {hasCtas ? (
                <div className="d-flex flex-wrap gap-2 gap-md-3">
                  {ctas.map((cta) => (
                    <Button
                      key={`${cta.label}-${cta.to}`}
                      href={cta.to}
                      variant={cta.variant ?? 'outline-secondary'}>
                      {cta.label}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
